

















































import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { BTabs, BTab } from 'bootstrap-vue';
import { isEqual, merge } from 'lodash';
import ZenButton from '@reciprocity/button';
import LoadingSpinner from '@reciprocity/loading-spinner';
import ConnectorBox from '../../box/ConnectorBox.vue';
import IconBox from '../../box/IconBox.vue';

import {
  loadSamlIdpSettings,
  saveSamlIdpSettings,
  refreshSamlSpSettings,
  createSamlSpCert,
  deleteSamlSpCert,
} from '@/utils/crudActions';

import { SamlSettings } from '../types';
import SettingsTab from './components/SettingsTab.vue';
import AdvanceSettingsTab from './components/AdvanceSettingsTab.vue';

const TABS = ['settings', 'advance-settings'];

export default Vue.extend({
  name: 'SAMLSettingsPage',
  components: {
    SettingsTab,
    AdvanceSettingsTab,
    BTab,
    BTabs,
    LoadingSpinner,
    ZenButton,
    ConnectorBox,
    IconBox,
  },
  props: {
    tab: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      saveIdleTimer: null,
      loading: <boolean>true,
      validations: <string[]>[],
      samlSettings: <SamlSettings>{
        url: '',
        fileContent: '',
        advance: {
          security: {
            nameIdEncrypted: false,
            authnRequestsSigned: false,
            logoutRequestSigned: true,
            logoutResponseSigned: true,
            signMetadata: false,
            wantMessagesSigned: false,
            wantAssertionsSigned: false,
            wantAssertionsEncrypted: false,
            wantNameId: true,
            wantNameIdEncrypted: false,
            wantAttributeStatement: false,
            requestedAuthnContext: true,
            requestedAuthnContextComparison: 'exact',
            failOnAuthnContextMismatch: false,
            metadataValidUntil: null, // Default to 2 days.
            metadataCacheDuration: null, // Default to one week.
            signatureAlgorithm: 'http://www.w3.org/2001/04/xmldsig-more#rsa-sha256',
            digestAlgorithm: 'http://www.w3.org/2001/04/xmlenc#sha256',
          },
        },
        idpCerts: [],
        idpEntityId: '',
        idpSloUrl: '',
        idpSsoUrl: '',
        // Service provider data
        spCerts: [],
        spEntityId: '',
        spAssertionConsumerUrl: '',
        spSloUrl: '',
        spNameIdFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified',
        // Other data to create derived values
        zengrcUrl: '',
      },
    };
  },
  computed: {
    tabIndex: {
      get() {
        return TABS.indexOf(this.tab) || 0;
      },
      set(tabIndex: number) {
        if (tabIndex != this.tabIndex) {
          if (tabIndex == 2) {
            this.showAlert({ type: 'success', message: 'SAML authentication should be ready' });
            this.$router.push({ name: 'authenticationSettings' });
          } else {
            this.$router.push({
              name: 'authenticationSettingsSaml',
              params: {
                tab: TABS[tabIndex],
              },
            });
          }
        }
      },
    },
  },
  ...mapGetters(['csrfToken', 'routes']),
  async created() {
    this.setPageTitle({ title: 'Configure custom SAML authentication' });
    this.syncSettings();
  },
  methods: {
    ...mapActions({
      setPageTitle: 'setPageTitle',
      showAlert: 'alerts/show',
    }),
    updateSamlSettings(samlSettings: SamlSettings, save: boolean = true) {
      if (!isEqual(this.samlSettings, samlSettings)) {
        this.samlSettings = samlSettings;
        // If metadata file provided, save inmediatly else set a timer or clear the timer
        if (samlSettings.url || samlSettings.fileContent) this.saveSettings();
        else if (save) this.autoSaveSettings();
        else clearTimeout(this.saveIdleTimer);
      }
    },
    autoSaveSettings() {
      // If no new event happens, save will take place after s sec.
      clearTimeout(this.saveIdleTimer);
      this.saveIdleTimer = setTimeout(this.saveSettings, 2000);
    },
    async saveSettings() {
      try {
        const samlSettings = await this.saveSamlIdpSettings(this.samlSettings, this.csrfToken);
        this.samlSettings = { ...this.samlSettings, url: '', fileContent: '', ...samlSettings };
        this.showAlert({ type: 'success', message: 'Settings saved successfully' });
      } catch (error) {
        this.showAlert(error);
      }
    },
    async syncSettings() {
      try {
        this.loading = true;
        // 1.- Load server data and merge with page defined defaults.
        const samlSettings = merge({}, this.samlSettings, await this.loadSamlIdpSettings());
        // 2.- Save merged valued to server.
        this.samlSettings = await this.saveSamlIdpSettings(samlSettings, this.csrfToken);
      } catch (error) {
        this.showAlert(error);
      } finally {
        this.loading = false;
      }
    },
    async refreshSpSettings() {
      try {
        this.samlSettings = { ...this.samlSettings, ...(await this.refreshSamlSpSettings(this.csrfToken)) };
        this.showAlert({ type: 'success', message: 'SP settings have been refreshed' });
      } catch (error) {
        this.showAlert(error);
      }
    },
    async createSpCert() {
      try {
        this.samlSettings = { ...this.samlSettings, ...(await this.createSamlSpCert(this.csrfToken)) };
        this.showAlert({ type: 'success', message: 'New SP certificate has been created' });
      } catch (error) {
        this.showAlert(error);
      }
    },
    async deleteSpCert(datetime: string) {
      try {
        this.samlSettings = { ...this.samlSettings, ...(await this.deleteSamlSpCert(datetime, this.csrfToken)) };
        this.showAlert({ type: 'success', message: 'SP certificate has been deleted' });
      } catch (error) {
        this.showAlert(error);
      }
    },
    next() {
      this.tabIndex++;
    },
    prev() {
      this.$router.go(-1);
    },
    loadSamlIdpSettings,
    saveSamlIdpSettings,
    refreshSamlSpSettings,
    createSamlSpCert,
    deleteSamlSpCert,
  },
});
